import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  AlertTriangle, ArrowLeft, Check, CheckCircle2, Copy, Download, FileSearch,
  FileText, History, Loader2, Plus, ShieldCheck, Sparkles, Upload,
} from "lucide-react";

const errMsg = (err, fallback) => formatApiError(err?.response?.data?.detail) || fallback;
const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const DOC_TYPES = [
  { id: "bolletta", label: "Bolletta energia / gas", hint: "POD/PDR, consumi, totale" },
  { id: "fattura", label: "Fattura generica", hint: "P.IVA, imponibile, IVA, scadenza" },
  { id: "generico", label: "Documento generico", hint: "campi proposti dall'AI" },
];
const TYPE_LABEL = { bolletta: "Bolletta", fattura: "Fattura", generico: "Generico" };

const download = (name, mime, text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: mime }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const ExportButtons = ({ fields, filename }) => {
  const rows = fields.map((f) => [f.label, f.value]);
  const base = (filename || "documento").replace(/\.[^.]+$/, "");
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button type="button" data-testid="export-copy"
              className="wl-btn wl-btn--ghost wl-btn--sm"
              onClick={() => {
                navigator.clipboard.writeText(rows.map((r) => r.join("\t")).join("\n"));
                toast.success("Dati copiati negli appunti");
              }}>
        <Copy size={13} className="mr-1" /> Copia
      </button>
      <button type="button" data-testid="export-csv"
              className="wl-btn wl-btn--ghost wl-btn--sm"
              onClick={() => download(`${base}_dati.csv`, "text/csv;charset=utf-8",
                "\uFEFFcampo;valore\n" + rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n"))}>
        <Download size={13} className="mr-1" /> CSV
      </button>
      <button type="button" data-testid="export-json"
              className="wl-btn wl-btn--ghost wl-btn--sm"
              onClick={() => download(`${base}_dati.json`, "application/json",
                JSON.stringify(Object.fromEntries(fields.map((f) => [f.key, f.value])), null, 2))}>
        <Download size={13} className="mr-1" /> JSON
      </button>
    </div>
  );
};

export default function AnalyzeDoc() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const [sid, setSid] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [docType, setDocType] = useState("bolletta");
  const [fields, setFields] = useState([]);
  const [docSummary, setDocSummary] = useState("");
  const [status, setStatus] = useState("empty"); // empty | ready | extracted | confirmed
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const allowed = user?.role === "super_admin" || features?.ai_tools;

  const [historyFailed, setHistoryFailed] = useState(false);
  const loadHistory = () =>
    api.get("/ai/tools/analyzedoc/sessions").then(({ data }) => { setHistory(data); setHistoryFailed(false); }).catch(() => setHistoryFailed(true));
  useEffect(() => { if (allowed) loadHistory(); }, [allowed]);

  if (features && !allowed) return <Navigate to="/services" replace />;

  const reset = () => {
    setSid(null); setFileInfo(null); setFields([]); setDocSummary("");
    setStatus("empty"); setDocType("bolletta");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      let id = sid;
      if (!id || status === "confirmed") {
        const { data } = await api.post("/ai/tools/analyzedoc/sessions");
        id = data.id;
        setSid(id);
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/tools/analyzedoc/sessions/${id}/file`, fd);
      setFileInfo(data.file);
      setFields([]); setDocSummary(""); setStatus("ready");
    } catch (err) {
      toast.error(errMsg(err, "Caricamento non riuscito"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onExtract = async () => {
    setExtracting(true);
    try {
      const { data } = await api.post(`/ai/tools/analyzedoc/sessions/${sid}/extract`, { doc_type: docType });
      setFields(data.fields.map((f) => ({ ...f, verified: false })));
      setDocSummary(data.doc_summary || "");
      setStatus("extracted");
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Estrazione non riuscita"));
    } finally {
      setExtracting(false);
    }
  };

  const setField = (key, patch) =>
    setFields((fs) => fs.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const toVerify = fields.filter((f) => !f.verified).length;

  const onConfirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/ai/tools/analyzedoc/sessions/${sid}/confirm`, {
        fields: fields.map((f) => ({ key: f.key, label: f.label, value: f.value })),
      });
      setStatus("confirmed");
      toast.success("Scheda confermata e salvata nello storico");
      loadHistory();
    } catch (err) {
      toast.error(errMsg(err, "Conferma non riuscita"));
    } finally {
      setConfirming(false);
    }
  };

  const openHistory = async (id) => {
    try {
      const { data } = await api.get(`/ai/tools/analyzedoc/sessions/${id}`);
      setSid(data.id);
      setFileInfo(data.file);
      setDocType(data.doc_type || "bolletta");
      setDocSummary(data.doc_summary || "");
      if (data.status === "confirmed") {
        setFields((data.confirmed_fields || []).map((f) => ({ ...f, confidence: 1, needs_review: false, verified: true })));
        setStatus("confirmed");
      } else if (data.status === "extracted") {
        setFields((data.fields || []).map((f) => ({ ...f, verified: false })));
        setStatus("extracted");
      } else {
        setFields([]); setStatus("ready");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err, "Analisi non trovata"));
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="analyzedoc-page">
      <div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary hover:text-text-primary transition-colors mb-4"
              data-testid="analyzedoc-back">
          <ArrowLeft size={14} /> Assistente AI
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-medium tracking-tight text-text-primary flex items-center gap-2">
              <FileSearch size={22} className="text-accent-strong" /> Analizza documento
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Carica una bolletta, una fattura o un documento: l'AI estrae i dati, tu li verifichi e confermi.
            </p>
          </div>
          {(status !== "empty") && (
            <button type="button" onClick={reset} className="wl-btn wl-btn--ghost wl-btn--sm"
                    data-testid="new-analysis-btn">
              <Plus size={14} className="mr-1" /> Nuova analisi
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2.5 text-[12px] text-text-secondary bg-surface-strong border border-border rounded-lg px-3.5 py-2.5"
           data-testid="privacy-note">
        <ShieldCheck size={15} className="text-accent-strong flex-shrink-0 mt-0.5" />
        <span>
          Per estrarre i dati, il documento viene inviato <strong>integralmente</strong> al fornitore AI
          tramite API a pagamento che <strong>non usa i tuoi dati per addestrare i modelli</strong>.
          Non caricare documenti che non vuoi condividere. Nulla viene salvato senza la tua conferma.
        </span>
      </div>

      {/* Step 1: upload + tipo */}
      <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {DOC_TYPES.map((t) => (
            <button key={t.id} type="button" onClick={() => setDocType(t.id)}
                    disabled={status === "confirmed"}
                    data-testid={`doc-type-${t.id}`}
                    className={`text-left rounded-lg border px-3.5 py-2.5 transition-colors ${
                      docType === t.id ? "border-accent bg-accent-subtle" : "border-border hover:border-accent/50"}`}>
              <div className="text-sm font-medium text-text-primary">{t.label}</div>
              <div className="text-[11px] text-text-muted">{t.hint}</div>
            </button>
          ))}
        </div>

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
               onChange={(e) => onUpload(e.target.files?.[0])} data-testid="analyzedoc-file-input" />
        {!fileInfo ? (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="analyzedoc-upload-btn"
                  className="w-full border-2 border-dashed border-border rounded-xl py-8 flex flex-col items-center gap-2 text-text-secondary hover:border-accent hover:text-text-primary transition-colors">
            {uploading ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
            <span className="text-sm font-medium">Carica PDF o foto (JPG, PNG)</span>
            <span className="text-[11px] text-text-muted">Max 10 MB · 20 pagine</span>
          </button>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 text-sm" data-testid="analyzedoc-file-chip">
              <FileText size={16} className="text-accent-strong" />
              <span className="font-medium text-text-primary">{fileInfo.filename}</span>
              <span className="text-[11px] text-text-muted">
                {fileInfo.pages} {fileInfo.pages === 1 ? "pagina" : "pagine"} · {(fileInfo.size_bytes / 1024).toFixed(0)} KB
              </span>
              {status !== "confirmed" && (
                <button type="button" className="text-[11px] text-accent-strong hover:underline"
                        onClick={() => fileRef.current?.click()} data-testid="analyzedoc-replace-file">
                  sostituisci
                </button>
              )}
            </div>
            {status === "ready" && (
              <button type="button" onClick={onExtract} disabled={extracting}
                      className="wl-btn wl-btn--primary" data-testid="extract-btn">
                {extracting ? (<><Loader2 size={14} className="animate-spin mr-1.5" /> Lettura in corso…</>)
                  : (<><Sparkles size={14} className="mr-1.5" /> Estrai i dati</>)}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Step 2: scheda campi */}
      {(status === "extracted" || status === "confirmed") && (
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4" data-testid="fields-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-medium text-text-primary">
                {status === "confirmed" ? "Scheda confermata" : "Verifica i dati estratti"}
              </h2>
              {docSummary && <p className="text-[12px] text-text-secondary mt-0.5">{docSummary}</p>}
            </div>
            <ExportButtons fields={fields} filename={fileInfo?.filename} />
          </div>

          {status === "extracted" && (
            <p className="text-[12px] text-text-muted">
              Controlla ogni campo (correggi se serve) e spunta la casella per segnarlo come verificato.
              I campi evidenziati hanno una confidenza bassa: <strong>ricontrollali sul documento</strong>.
            </p>
          )}

          <div className="space-y-1.5" data-testid="fields-list">
            {fields.map((f) => (
              <div key={f.key}
                   data-testid={`field-row-${f.key}`}
                   className={`grid grid-cols-[minmax(120px,1fr)_2fr_auto] items-center gap-3 rounded-lg border px-3 py-2 ${
                     f.needs_review && !f.verified ? "border-amber-300 bg-amber-50/60" : "border-border"}`}>
                <div>
                  <div className="text-[12px] font-medium text-text-primary">{f.label}</div>
                  {status !== "confirmed" && (
                    f.needs_review ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700"
                            data-testid={`field-review-badge-${f.key}`}>
                        <AlertTriangle size={10} /> da verificare · conf. {(f.confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-text-muted">conf. {(f.confidence * 100).toFixed(0)}%</span>
                    )
                  )}
                </div>
                <input value={f.value} disabled={status === "confirmed"}
                       onChange={(e) => setField(f.key, { value: e.target.value, verified: true })}
                       data-testid={`field-input-${f.key}`}
                       className="h-8 text-sm bg-surface border border-border rounded px-2.5 focus:outline-none focus:border-accent disabled:bg-subtle disabled:text-text-secondary font-mono" />
                {status === "confirmed" ? (
                  <CheckCircle2 size={16} className="text-success" />
                ) : (
                  <label className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary cursor-pointer select-none">
                    <input type="checkbox" checked={f.verified}
                           onChange={(e) => setField(f.key, { verified: e.target.checked })}
                           data-testid={`field-verify-${f.key}`}
                           className="accent-[var(--wl-accent,#2563eb)]" />
                    verificato
                  </label>
                )}
              </div>
            ))}
          </div>

          {status === "extracted" && (
            <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
              <span className="text-[12px] text-text-muted" data-testid="verify-counter">
                {toVerify === 0 ? "Tutti i campi verificati" :
                  `${toVerify} ${toVerify === 1 ? "campo da verificare" : "campi da verificare"}`}
              </span>
              <button type="button" onClick={onConfirm} disabled={toVerify > 0 || confirming}
                      className="wl-btn wl-btn--primary" data-testid="confirm-btn">
                {confirming ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                Conferma e salva
              </button>
            </div>
          )}
          {status === "confirmed" && (
            <div className="flex items-center gap-2 text-[12px] text-success" data-testid="confirmed-banner">
              <CheckCircle2 size={14} /> Salvata nello storico il {fmtDT(history.find((h) => h.id === sid)?.confirmed_at) || "ora"}
            </div>
          )}
        </div>
      )}

      {/* Storico */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="analyzedoc-history">
        <h2 className="text-base font-medium text-text-primary flex items-center gap-2 mb-3">
          <History size={15} className="text-text-muted" /> Storico analisi
        </h2>
        {historyFailed ? (
          <LoadErrorBox compact what="lo storico analisi" onRetry={loadHistory} testId="analyzedoc-history-error" />
        ) : history.length === 0 ? (
          <p className="text-sm text-text-muted">Nessuna analisi ancora: carica il primo documento qui sopra.</p>
        ) : (
          <div className="divide-y divide-border">
            {history.map((h) => (
              <button key={h.id} type="button" onClick={() => openHistory(h.id)}
                      data-testid={`history-item-${h.id}`}
                      className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-subtle rounded px-2 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText size={14} className="text-text-muted flex-shrink-0" />
                  <span className="text-sm text-text-primary truncate">{h.filename}</span>
                  {h.doc_type && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-subtle text-text-secondary flex-shrink-0">
                      {TYPE_LABEL[h.doc_type]}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-[11px] text-text-muted">
                  <span>{h.n_fields} campi</span>
                  {h.status === "confirmed"
                    ? <span className="text-success font-medium">confermata</span>
                    : <span className="text-amber-600">da confermare</span>}
                  <span>{fmtDT(h.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
